/* ==========================================================================
   Housecore Horror Fest page behaviour: countdown, film day tabs, FAQ
   accordion, on-page add to cart, pre-sale mode and the sticky offset under
   the theme's fixed header.

   Every hhf section includes this file, so it guards against running twice.
   All interaction uses document-level delegation, which means sections that
   the theme editor re-renders keep working without re-binding anything.
   ========================================================================== */
(function () {
  if (window.HHF) return;
  window.HHF = {};

  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  function pad(n) { return String(n).padStart(2, '0'); }

  /* ---------- Countdown (one shared 1s ticker for every countdown on the page) ---------- */
  function tick() {
    document.querySelectorAll('[data-hhf-countdown]').forEach(function (el) {
      var target = Date.parse(el.getAttribute('data-hhf-countdown'));
      if (isNaN(target)) return;
      var diff = Math.max(0, target - Date.now());
      var d = Math.floor(diff / 864e5); diff -= d * 864e5;
      var h = Math.floor(diff / 36e5); diff -= h * 36e5;
      var m = Math.floor(diff / 6e4); diff -= m * 6e4;
      var s = Math.floor(diff / 1e3);
      var vals = { d: d, h: h, m: m, s: s };
      el.querySelectorAll('[data-unit]').forEach(function (n) {
        n.textContent = pad(vals[n.getAttribute('data-unit')]);
      });
      // Plain-language line for screen readers (minutes are precise enough; only
      // rewritten when it changes, and never announced on its own: no aria-live).
      var sr = el.querySelector('[data-hhf-countdown-sr]');
      if (sr) {
        var unit = function (n, word) { return n + ' ' + word + (n === 1 ? '' : 's'); };
        var text = (sr.getAttribute('data-prefix') || 'Time remaining') + ': ' +
          unit(d, 'day') + ', ' + unit(h, 'hour') + ', ' + unit(m, 'minute');
        if (sr.textContent !== text) sr.textContent = text;
      }
    });
  }

  /* ---------- Pre-sale mode: the hero section carries data-hhf-presale when
     "Tickets on sale" is unticked; every buy button then reads "Notify me". */
  function syncPresale() {
    document.documentElement.classList.toggle('hhf-presale', !!document.querySelector('[data-hhf-presale]'));
  }

  /* ---------- Sticky offset: once you scroll past it, the theme pins its header
     (theme.js adds .header-fixed-top to #shopify-section-header), so the sub nav has
     to stick just below it instead of at top:0. Measured live because the
     header can change height (mobile menu, scroll state). */
  var stickyQueued = false;
  function measureSticky() {
    stickyQueued = false;
    var offset = 0;
    document.querySelectorAll('#shopify-section-header, #shopify-section-header > *, .fixed-nav').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position !== 'fixed' && cs.position !== 'sticky') return;
      if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') return;
      var r = el.getBoundingClientRect();
      // Only a bar pinned to the top counts. Skip full-height overlays like the
      // theme's mobile menu drawer, which is fixed but covers the whole screen.
      if (r.height === 0 || r.top > 1 || r.height > window.innerHeight * 0.4) return;
      if (r.bottom > offset) offset = r.bottom;
    });
    document.documentElement.style.setProperty('--hhf-sticky-top', Math.round(offset) + 'px');
  }
  function queueSticky() {
    if (stickyQueued) return;
    stickyQueued = true;
    requestAnimationFrame(measureSticky);
  }

  /* ---------- Screen-reader announcements (one shared polite live region) ---------- */
  var live = null;
  function ensureLive() {
    live = document.querySelector('[data-hhf-live]');
    if (live) return;
    live = document.createElement('div');
    live.className = 'hhf-sr';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    live.setAttribute('data-hhf-live', '');
    document.body.appendChild(live);
  }
  function announce(msg) {
    if (!live) ensureLive();
    // Clear first so the same message twice in a row is still read out.
    live.textContent = '';
    setTimeout(function () { live.textContent = msg; }, 60);
  }

  /* ---------- Cart ---------- */
  // Keeps the hidden ": <product name>" after the visible label, so screen readers
  // still know which item a button is for once it reads "Added" / "Sold out".
  function setLabel(btn, text) {
    btn.textContent = text;
    var name = btn.getAttribute('data-hhf-name');
    if (name) {
      var sr = document.createElement('span');
      sr.className = 'hhf-sr';
      sr.textContent = ': ' + name;
      btn.appendChild(sr);
    }
  }

  function updateCartCount() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Theme header markup: <a class="cartCount"> … <span class="badge">N</span></a>
        document.querySelectorAll('.cartCount').forEach(function (a) {
          var badge = a.querySelector('.badge');
          if (!badge) {
            badge = document.createElement('span');
            badge.className = 'badge';
            a.appendChild(document.createTextNode(' '));
            a.appendChild(badge);
          }
          badge.textContent = cart.item_count;
        });
      })
      .catch(function () {});
  }

  function addToCart(btn) {
    // Mockup behaviour: once added, a button just reads "Added" and further clicks do nothing.
    if (btn.disabled || btn.getAttribute('data-added') === 'true' || btn.getAttribute('aria-busy') === 'true') return;

    var buy = btn.closest('[data-hhf-buy]');
    var select = buy && buy.querySelector('[data-hhf-variant]');
    var variantId = Number(select ? select.value : btn.getAttribute('data-variant'));
    if (!variantId) return;

    var key = btn.getAttribute('data-hhf-key');
    var twins = key ? document.querySelectorAll('[data-hhf-add][data-hhf-key="' + key + '"]') : [btn];

    btn.setAttribute('aria-busy', 'true');
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (r) {
        return r.json().then(function (body) { return { ok: r.ok, status: r.status, body: body }; });
      })
      .then(function (res) {
        btn.removeAttribute('aria-busy');
        var name = btn.getAttribute('data-hhf-name');
        if (res.ok) {
          // Package button + mobile buy bar share a key, so both flip to "Added" together.
          twins.forEach(function (b) { b.setAttribute('data-added', 'true'); setLabel(b, 'Added'); });
          updateCartCount();
          announce(name ? 'Added to cart: ' + name : 'Added to cart');
        } else if (res.status === 422) {
          setLabel(btn, 'Sold out');
          btn.disabled = true;
          announce(name ? 'Sold out: ' + name : 'Sold out');
        } else {
          announce('Could not add to cart. Please try again.');
        }
      })
      .catch(function () {
        btn.removeAttribute('aria-busy');
        announce('Could not add to cart. Please try again.');
      });
  }

  /* ---------- Film day tabs (ARIA tabs pattern) ---------- */
  // Only the selected tab is in the Tab order (tabindex 0); the others are -1 and are
  // reached with the arrow keys, which is what screen-reader users expect from role="tab".
  function selectTab(tab, focus) {
    var tabs = tab.closest('[data-hhf-tabs]');
    var id = tab.getAttribute('data-hhf-tab');
    tabs.querySelectorAll('[data-hhf-tab]').forEach(function (t) {
      var on = t === tab;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.setAttribute('tabindex', on ? '0' : '-1');
    });
    tabs.querySelectorAll('[data-hhf-panel]').forEach(function (p) {
      p.hidden = p.getAttribute('data-hhf-panel') !== id;
    });
    if (focus) tab.focus();
  }

  document.addEventListener('keydown', function (e) {
    var tab = e.target.closest && e.target.closest('[data-hhf-tab]');
    if (!tab) return;
    var all = Array.prototype.slice.call(tab.closest('[data-hhf-tabs]').querySelectorAll('[data-hhf-tab]'));
    var i = all.indexOf(tab), next = null;
    if (e.key === 'ArrowRight') next = all[(i + 1) % all.length];
    else if (e.key === 'ArrowLeft') next = all[(i - 1 + all.length) % all.length];
    else if (e.key === 'Home') next = all[0];
    else if (e.key === 'End') next = all[all.length - 1];
    if (!next) return;
    e.preventDefault();
    selectTab(next, true);
  });

  /* ---------- Delegated clicks ---------- */
  document.addEventListener('click', function (e) {
    // Film effect pause / play (WCAG 2.2.2). Only runs when pressed; the CSS freezes the
    // overlay's animations in place while <html> has .hhf-fx-paused and resumes on Play.
    var fx = e.target.closest('[data-hhf-fx-toggle]');
    if (fx) {
      var paused = document.documentElement.classList.toggle('hhf-fx-paused');
      document.querySelectorAll('[data-hhf-fx-toggle] [data-hhf-fx-verb]').forEach(function (v) {
        v.textContent = paused ? 'Play' : 'Pause';
      });
      var noun = fx.querySelector('.hhf-fx-toggle__noun');
      noun = noun ? noun.textContent.trim() : 'effect';
      announce(noun.charAt(0).toUpperCase() + noun.slice(1) + (paused ? ' paused' : ' playing'));
      return;
    }

    var add = e.target.closest('[data-hhf-add]');
    if (add) {
      e.preventDefault();
      if (document.documentElement.classList.contains('hhf-presale')) {
        var list = document.querySelector('[data-hhf-newsletter]');
        if (list) list.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      addToCart(add);
      return;
    }

    var tab = e.target.closest('[data-hhf-tab]');
    if (tab) {
      selectTab(tab);
      return;
    }

    var q = e.target.closest('[data-hhf-faq-q]');
    if (q) {
      // Single-open accordion, as in the mockup.
      var item = q.closest('[data-hhf-faq-item]');
      var wasOpen = q.getAttribute('aria-expanded') === 'true';
      q.closest('[data-hhf-faq]').querySelectorAll('[data-hhf-faq-item]').forEach(function (it) {
        var open = it === item && !wasOpen;
        it.querySelector('[data-hhf-faq-q]').setAttribute('aria-expanded', open ? 'true' : 'false');
        it.querySelector('[data-hhf-faq-a]').hidden = !open;
        it.querySelector('[data-hhf-faq-icon]').textContent = open ? '–' : '+';
      });
    }
  });

  /* Variant picker: keep the shown price in step with the chosen variant. */
  document.addEventListener('change', function (e) {
    var select = e.target.closest('[data-hhf-variant]');
    if (!select) return;
    var buy = select.closest('[data-hhf-buy]');
    var opt = select.options[select.selectedIndex];
    var price = buy && buy.querySelector('[data-hhf-price]:not([data-hhf-price-fixed])');
    if (price && opt) price.textContent = opt.getAttribute('data-price');
  });

  function init() {
    ensureLive();
    syncPresale();
    tick();
    measureSticky();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  setInterval(tick, 1000);
  window.addEventListener('resize', queueSticky);
  window.addEventListener('scroll', queueSticky, { passive: true });
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', function () { setTimeout(syncPresale, 0); });
})();
